import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { DataStandardsSidebar } from '@/components/data-standards/DataStandardsSidebar';
import { SchemaViewer } from '@/components/data-standards/SchemaViewer';
import { useDataStandards } from '@/hooks/useDataStandards';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Database, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';

function SidebarSkeleton() {
  return (
    <div className="w-64 shrink-0 space-y-6">
      <Skeleton className="h-5 w-32" />
      {[1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <div className="ml-4 space-y-1">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SchemaViewerSkeleton() {
  return (
    <div className="flex-1 min-w-0 space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
        <Skeleton className="h-4 w-full max-w-xl" />
        <Skeleton className="h-3 w-32" />
      </div>
      {/* Tabs */}
      <Skeleton className="h-10 w-72" />
      {/* Code block */}
      <Skeleton className="h-64 w-full rounded-lg" />
      {/* Fields section */}
      <div className="space-y-4">
        <Skeleton className="h-6 w-32" />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function DataStandards() {
  const { schemas, categories, isLoading, getSchemaById } = useDataStandards();
  const { trackStandardView } = useAnalytics();
  const [searchParams] = useSearchParams();
  const [activeSchemaId, setActiveSchemaId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const schemaSlugFromUrl = searchParams.get('schema');

  // Set schema from URL param or default when data loads
  useEffect(() => {
    if (schemas.length === 0) return;
    
    // If URL has schema param, find and set it (schema.id IS the slug)
    if (schemaSlugFromUrl) {
      const schemaFromUrl = schemas.find(s => s.id === schemaSlugFromUrl);
      if (schemaFromUrl) {
        setActiveSchemaId(schemaFromUrl.id);
        return;
      }
    }
    
    // Fallback to default (first schema) only if no active schema
    if (!activeSchemaId && categories.length > 0 && categories[0].schemas.length > 0) {
      setActiveSchemaId(categories[0].schemas[0].id);
    }
  }, [schemas, categories, schemaSlugFromUrl, activeSchemaId]);

  const activeSchema = activeSchemaId ? getSchemaById(activeSchemaId) : undefined;

  // Track standard views
  useEffect(() => {
    if (activeSchema) {
      trackStandardView(activeSchema.id);
    }
  }, [activeSchema?.id, trackStandardView]);

  const handleSchemaSelect = (schemaId: string) => {
    setActiveSchemaId(schemaId);
    setSidebarOpen(false); // Close mobile sidebar on selection
  };

  return (
    <Layout>
      <div className="container py-4 md:py-8">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Database className="h-6 w-6 md:h-8 md:w-8 text-primary" />
            <h1 className="text-2xl md:text-3xl font-bold">API Standards</h1>
          </div>
          <p className="text-muted-foreground text-base md:text-lg max-w-3xl">
            Unified data structures for automotive API integration. Use these standards to ensure 
            seamless data exchange between systems and APIs.
          </p>
        </div>

        {/* Content */}
        <div className="flex gap-4 lg:gap-8">
          {/* Desktop sidebar - hidden on mobile */}
          <div className="hidden lg:block">
            {isLoading ? (
              <SidebarSkeleton />
            ) : (
              <DataStandardsSidebar
                categories={categories}
                activeSchemaId={activeSchemaId || ''}
                onSchemaSelect={setActiveSchemaId}
              />
            )}
          </div>
          
          <main className="flex-1 min-w-0">
            {isLoading ? (
              <SchemaViewerSkeleton />
            ) : activeSchema ? (
              <SchemaViewer schema={activeSchema} />
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                Select a schema from the sidebar to view its structure.
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile floating nav */}
      <div className="lg:hidden fixed bottom-4 right-4 z-50">
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetTrigger asChild>
            <Button size="icon" className="rounded-full shadow-lg h-12 w-12">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle schemas menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 pt-12">
            {isLoading ? (
              <SidebarSkeleton />
            ) : (
              <DataStandardsSidebar
                categories={categories}
                activeSchemaId={activeSchemaId || ''}
                onSchemaSelect={handleSchemaSelect}
                isMobile
              />
            )}
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
